import React, { useEffect, useState } from "react";
import L from "leaflet";
import { MapContainer, TileLayer, useMap, Marker, Polyline, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "./css/CustomMap.css";
import "leaflet-routing-machine";
import axios from "axios";
import { API_ENDPOINTS } from '../../config/apiConfig';

// Define paths to marker icons - using direct path references instead of imports
const MARKER_PATHS = {
  depotMarker: '/img/map-markers/depot-marker.svg',
  chargerMarker: '/img/map-markers/charger-marker.svg',
  selectedDepotMarker: '/img/map-markers/depot-marker-selected.svg',
  selectedChargerMarker: '/img/map-markers/charger-marker-selected.svg',
  markerShadow: '/img/map-markers/marker-shadow.png'
};

// ==== Constants ====
const CIRCUIT_COLORS = [
  '#FF0000', '#00FF00', '#0000FF', '#FFA500', '#800080', 
  '#00FFFF', '#FF00FF', '#FFD700', '#008000', '#4B0082',
];

// Define a fixed array of 20 distinct colors for routes
const ROUTE_COLORS = [
  '#FF5733', '#33FF57', '#3357FF', '#FF33F5', '#F5FF33', 
  '#33FFF5', '#FF5733', '#8C33FF', '#FF8C33', '#33FF8C', 
  '#338CFF', '#FF338C', '#8CFF33', '#F533FF', '#33F5FF', 
  '#FFA333', '#33FFA3', '#A333FF', '#FFA3A3', '#A3FFA3'
];

// ==== Marker Icons ====
const markerIcons = {
  charger: {
    default: new L.Icon({
      iconUrl: MARKER_PATHS.chargerMarker,
      shadowUrl: MARKER_PATHS.markerShadow,
      iconSize: [30, 49], // Increased size for charger by 20%
      iconAnchor: [15, 38], // Adjusted anchor point proportionally
      popupAnchor: [1, -41], // Adjusted popup position for larger icon
      shadowSize: [49, 49], // Shadow size matches icon
      shadowAnchor: [15, 49] // Shadow anchor aligned with icon
    }),
    selected: new L.Icon({
      iconUrl: MARKER_PATHS.selectedChargerMarker,
      shadowUrl: MARKER_PATHS.markerShadow,
      iconSize: [30, 49], // Increased size for charger by 20%
      iconAnchor: [15, 38], // Adjusted anchor point proportionally
      popupAnchor: [1, -41], // Adjusted popup position for larger icon
      shadowSize: [49, 49], // Shadow size matches icon
      shadowAnchor: [15, 49] // Shadow anchor aligned with icon
    })
  },
  depot: {
    default: new L.Icon({
      iconUrl: MARKER_PATHS.depotMarker,
      shadowUrl: MARKER_PATHS.markerShadow,
      iconSize: [45, 73], // Larger size for depot
      iconAnchor: [22, 58], // Adjusted anchor point proportionally
      popupAnchor: [1, -61], // Adjusted popup position for larger icon
      shadowSize: [73, 73], // Shadow size matches icon
      shadowAnchor: [22, 73] // Shadow anchor aligned with icon
    }),
    selected: new L.Icon({
      iconUrl: MARKER_PATHS.selectedDepotMarker,
      shadowUrl: MARKER_PATHS.markerShadow,
      iconSize: [45, 73], // Larger size for depot
      iconAnchor: [22, 58], // Adjusted anchor point proportionally
      popupAnchor: [1, -61], // Adjusted popup position for larger icon
      shadowSize: [73, 73], // Shadow size matches icon
      shadowAnchor: [22, 73] // Shadow anchor aligned with icon
    })
  }
};

// Controls overlay component
const MapControls = ({ 
  routeCheckbox, setRouteCheckbox,
  depotCheckbox, setDepotCheckbox,
  chargerCheckbox, setChargerCheckbox,
  satelliteView, setSatelliteView
}) => {
  return (
    <div className="map-overlay">
      <h3>Features</h3>
      <label className="overlay-entry">
        <input
          type="checkbox"
          className="checkbox-overlay"
          checked={routeCheckbox}
          onChange={(e) => setRouteCheckbox(e.target.checked)}
        />
        <span className="map-checkbox-description">Routes</span>
      </label>
      <label className="overlay-entry">
        <input
          type="checkbox"
          className="checkbox-overlay"
          checked={depotCheckbox}
          onChange={(e) => setDepotCheckbox(e.target.checked)}
        />
        <span className="map-checkbox-description">
          Depots
          <img 
            src={MARKER_PATHS.selectedDepotMarker}
            alt="Depot"
            style={{ width: "20px", height: "20px", marginLeft: "5px" }}
          />
        </span>
      </label>
      <label className="overlay-entry">
        <input
          type="checkbox"
          className="checkbox-overlay"
          checked={chargerCheckbox}
          onChange={(e) => setChargerCheckbox(e.target.checked)}
        />
        <span className="map-checkbox-description">
          Charging Stations
          <img 
            src={MARKER_PATHS.selectedChargerMarker}
            alt="Chargers"
            style={{ width: "15px", height: "15px", marginLeft: "5px" }}
          />
        </span>
      </label>
      <h3>Map Layer</h3>
      <label className="overlay-entry">
        <input
          type="checkbox"
          className="checkbox-overlay"
          checked={satelliteView}
          onChange={(e) => setSatelliteView(e.target.checked)}
        />
        <span className="map-checkbox-description">Satellite View</span>
      </label>
    </div>
  );
};

// Circuit lines renderer
const CircuitLinesLayer = ({ selectedEntities, circuitData }) => {
  useEffect(() => {
    console.log("Circuit data updated:", circuitData);
  }, [circuitData]);

  const renderCircuitLines = () => {
    const lines = [];
    Object.entries(selectedEntities).forEach(([depotId, isSelected]) => {
      if (isSelected && circuitData[depotId]) {
        circuitData[depotId].forEach((circuit, circuitIndex) => {
          const circuitColor = CIRCUIT_COLORS[circuitIndex % CIRCUIT_COLORS.length];
          
          // Safety check to ensure we have valid geometry
          if (!circuit.geometry || !circuit.geometry.coordinates) {
            console.warn(`Missing geometry for circuit ${circuit.circuit_name}`);
            return;
          }
          
          // Ensure coordinates is an array before proceeding
          if (!Array.isArray(circuit.geometry.coordinates)) {
            console.warn(`Invalid coordinates for circuit ${circuit.circuit_name}:`, circuit.geometry.coordinates);
            return;
          }
          
          circuit.geometry.coordinates.forEach((lineString, lineIndex) => {
            // Ensure lineString is an array of coordinates
            if (!Array.isArray(lineString)) {
              console.warn(`Invalid lineString for circuit ${circuit.circuit_name}:`, lineString);
              return;
            }
            
            const positions = lineString.map(coord => [coord[1], coord[0]]);
            lines.push(
              <Polyline
                key={`circuit-${depotId}-${circuitIndex}-${lineIndex}`}
                positions={positions}
                color={circuitColor}
                weight={3}
                opacity={0.8}
                eventHandlers={{
                  click: (e) => {
                    L.DomEvent.stopPropagation(e);
                  },
                }}
              >
                <Popup>
                  <div className="marker-popup">
                    <h5>Circuit</h5>
                    <table>
                      <tbody>
                        <tr>
                          <td>Name</td>
                          <td>{circuit.circuit_name}</td>
                        </tr>
                        <tr>
                          <td>Capacity</td>
                          <td>{circuit.capacity} kW</td>
                        </tr>
                        <tr>
                          <td>Voltage</td>
                          <td>{circuit.circuit_voltage} kV</td>
                        </tr>
                        <tr>
                          <td>Substation</td>
                          <td>{circuit.substation_name?.replace(/Kv/g, 'kV')}</td>
                        </tr>
                        <tr>
                          <td>Last Updated</td>
                          <td>{new Date(circuit.date_last_update).toLocaleDateString()}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Popup>
              </Polyline>
            );
          });
        });
      }
    });
    return lines;
  };

  return <>{renderCircuitLines()}</>;
};

// Depot markers layer
const DepotMarkersLayer = ({ depots, selectedEntities, getMarkerIcon, handleMarkerClick }) => {
  return (
    <>
      {depots.map((depot, index) => (
        <Marker
          key={`depot-${depot.depot_id || index}`}
          position={[depot.lat, depot.lon]}
          icon={getMarkerIcon(depot, 'depot')}
          zIndexOffset={selectedEntities[depot.depot_id] ? 1000 : 0}
          eventHandlers={{
            click: () => handleMarkerClick(depot.depot_id, true),
          }}
        >
          <Popup>
            <div className="marker-popup">
              <h5>Depot</h5>
              <table style={{width: '100%', tableLayout: 'fixed'}}>
                <colgroup>
                  <col style={{width: '20%'}} />
                  <col style={{width: '80%'}} />
                </colgroup>
                <tbody>
                  <tr><td>Name:</td><td>{depot.name}</td></tr>
                  <tr><td>Address:</td><td>{depot.address}</td></tr>
                </tbody>
              </table>
              <div style={{fontSize: '1.2rem', fontStyle: 'italic'}}>Click to view circuit lines on map</div>
            </div>
          </Popup>
        </Marker>
      ))}
    </>
  );
};

// Charger markers layer
const ChargerMarkersLayer = ({ chargers, selectedChargers, getMarkerIcon, handleChargerClick }) => {
  return (
    <>
      {chargers.map((charger, index) => (
        <Marker
          key={`charger-${charger.cs_id || index}-${charger.lat}-${charger.lon}`}
          position={[charger.lat, charger.lon]}
          icon={getMarkerIcon(charger, 'charger')}
          zIndexOffset={selectedChargers[charger.cs_id] ? 1000 : 0}
          eventHandlers={{
            click: () => handleChargerClick(charger.cs_id, true),
          }}
        >
          <Popup>
            <div className="marker-popup">
              <h5>Charging Station</h5>
              <table style={{width: '100%', tableLayout: 'fixed'}}>
                <colgroup>
                  <col style={{width: '40%'}} />
                  <col style={{width: '60%'}} />
                </colgroup>
                <tbody>
                  <tr><td>Owner</td><td>{charger.owner}</td></tr>
                  <tr><td>Address</td><td>{charger.addr}</td></tr>
                  <tr><td>Status</td><td>{charger.status}</td></tr>
                  <tr><td>Charger Count</td><td>{charger.stall_count}</td></tr>
                  <tr><td>Max Power (kW)</td><td>{charger.max_power}</td></tr>
                </tbody>
              </table>
            </div>
          </Popup>
        </Marker>
      ))}
    </>
  );
};

// Routes layer
const RoutesLayer = ({ routeData, highlightedRoute, routeColorMap, setRouteColorMap }) => {
  // Initialize the color map if it doesn't exist yet
  useEffect(() => {
    if (!routeColorMap || Object.keys(routeColorMap).length === 0) {
      const newColorMap = {};
      Object.keys(routeData || {}).forEach((routeId, index) => {
        newColorMap[routeId] = ROUTE_COLORS[index % ROUTE_COLORS.length];
      });
      if (Object.keys(newColorMap).length > 0) {
        setRouteColorMap(newColorMap);
      }
    }
  }, [routeData, routeColorMap, setRouteColorMap]);

  if (!routeData || typeof routeData !== 'object') {
    console.error("Invalid route data:", routeData);
    return null;
  }
  
  return (
    <>
      {Object.entries(routeData)
        .filter(([_, data]) => data && data.selected)
        .map(([routeId, data]) => {
          return (
            <RoutePolylines
              key={`route-${routeId}`}
              routeId={routeId}
              geometry={data.geometry}
              highlightedRoute={highlightedRoute}
              routeColor={routeColorMap[routeId] || ROUTE_COLORS[0]}
            />
          );
        })}
    </>
  );
};

// Create a new component to handle rendering route polylines
const RoutePolylines = ({ routeId, geometry, highlightedRoute, routeColor }) => {
  const map = useMap();
  
  useEffect(() => {
    if (!map) return;
    
    // Add more robust geometry checking
    if (!geometry || (Array.isArray(geometry) && geometry.length === 0)) {
      console.error(`No geometry data for route ${routeId}`);
      return;
    }
    
    // console.log(`Rendering route ${routeId} with geometry and color:`, geometry, routeColor);
    
    const layers = [];
    
    try {
      // Handle different possible geometry formats
      if (Array.isArray(geometry)) {
        // Process each link segment in the geometry
        geometry.forEach((link, linkIndex) => {
          // Check if link has coordinates
          if (link.coordinates && Array.isArray(link.coordinates) && link.coordinates.length > 1) {
            const positions = link.coordinates.map(coord => [coord[1], coord[0]]);
            
            const polyline = L.polyline(positions, {
              color: routeColor,
              weight: 6,
              opacity: 1,
              smoothFactor: 1,
            }).addTo(map);
            
            layers.push(polyline);
            
            // Add start point marker for this link
            const startCoord = positions[0];
            const startMarker = L.circleMarker(startCoord, {
              radius: 6,
              color: "#000000",
              weight: 2,
              fillColor: "#ffffff",
              fillOpacity: 1,
            }).addTo(map);
            layers.push(startMarker);
            
            // Add end point marker for this link
            const endCoord = positions[positions.length - 1];
            const endMarker = L.circleMarker(endCoord, {
              radius: 6,
              color: "#000000",
              weight: 2,
              fillColor: "#ffffff",
              fillOpacity: 1,
            }).addTo(map);
            layers.push(endMarker);
            
          } else if (link.lat && link.lon && Array.isArray(link.lat) && Array.isArray(link.lon)) {
            // Alternative format with separate lat/lon arrays
            const positions = [];
            for (let i = 0; i < Math.min(link.lat.length, link.lon.length); i++) {
              if (link.lat[i] !== null && link.lon[i] !== null) {
                positions.push([link.lat[i], link.lon[i]]);
              }
            }
            
            if (positions.length > 1) {
              const polyline = L.polyline(positions, {
                color: routeColor,
                weight: 6,
                opacity: 1,
                smoothFactor: 1,
              }).addTo(map);
              
              layers.push(polyline);
              
              // Add start point marker for this link
              const startCoord = positions[0];
              const startMarker = L.circleMarker(startCoord, {
                radius: 6,
                color: "#000000",
                weight: 2,
                fillColor: "#ffffff",
                fillOpacity: 1,
              }).addTo(map);
              layers.push(startMarker);
              
              // Add end point marker for this link
              const endCoord = positions[positions.length - 1];
              const endMarker = L.circleMarker(endCoord, {
                radius: 6,
                color: "#000000",
                weight: 2,
                fillColor: "#ffffff",
                fillOpacity: 1,
              }).addTo(map);
              layers.push(endMarker);
            }
          }
        });
      } else {
        console.error(`Unexpected geometry format for route ${routeId}:`, geometry);
      }
    } catch (error) {
      console.error(`Error rendering route ${routeId}:`, error);
    }
    
    // Cleanup function to remove all layers when unmounting
    return () => {
      layers.forEach(layer => {
        if (map && layer) {
          try {
            map.removeLayer(layer);
          } catch (e) {
            console.error("Error removing layer:", e);
          }
        }
      });
    };
  }, [map, geometry, routeId, routeColor, highlightedRoute]);
  
  return null;
};

// ==== Main Component ====
export const CustomMap = ({
  selectedRoutes,
  selectedEntities = {},
  selectedChargers = {},
  circuitData = {},
  onMarkerClick,
  onChargerClick,
  onRouteClick,
  highlightedRoute,
  depotList = [],
  chargerList = [],
  refreshTrigger = 0,
  loadingCircuits = false,
}) => {
  /* 
    Args:
      selectedRoutes: array of route ids
      selectedEntities: object with depot ids as keys and boolean values
      selectedChargers: object with charger ids as keys and boolean values
      circuitData: object with depot ids as keys and arrays of circuit objects as values
      onMarkerClick: function to handle marker clicks
      onChargerClick: function to handle charger clicks
      onRouteClick: function to handle route clicks
      highlightedRoute: route id of the highlighted route
      depotList: array of depot objects
      chargerList: array of charger objects
      refreshTrigger: number to trigger a refresh of the map
      loadingCircuits: boolean to indicate if circuits are still loading
  */
  // State management
  const [routeColorMap, setRouteColorMap] = useState({});
  
  // Feature visibility toggles
  const [routeCheckbox, setRouteCheckbox] = useState(true);
  const [depotCheckbox, setDepotCheckbox] = useState(true);
  const [chargerCheckbox, setChargerCheckbox] = useState(true);
  const [satelliteView, setSatelliteView] = useState(false);
  
  const authToken = localStorage.getItem("authToken");

  // Helper functions
  const getMarkerIcon = (item, type) => {
    if (type === 'depot') {
      const isSelected = selectedEntities[item.depot_id];
      return isSelected ? markerIcons[type].selected : markerIcons[type].default;
    } else if (type === 'charger') {
      const isSelected = selectedChargers[item.cs_id];
      return isSelected ? markerIcons[type].selected : markerIcons[type].default;
    }
    return markerIcons[type].default;
  };

  // Event handlers
  const handleMarkerClick = (depotId, fromMap = false) => {
    // Check if we received an object (batch selection from table)
    if (typeof depotId === 'object') {
      // This is a batch operation from the "select all" checkbox
      if (onMarkerClick) onMarkerClick(depotId, true);
    } else {
      // This is a single selection
      if (onMarkerClick) onMarkerClick(depotId, fromMap);
    }
  };

  const handleChargerClick = (chargerId, fromMap = false) => {
    // Check if we received an object (batch selection from table)
    if (typeof chargerId === 'object') {
      // This is a batch operation from the "select all" checkbox
      if (onChargerClick) onChargerClick(chargerId, true);
    } else {
      // This is a single selection
      if (onChargerClick) onChargerClick(chargerId, fromMap);
    }
  };

  if (!window.L) {
    return <div>Loading map library...</div>;
  }

  // Add a loading indicator for circuits
  const CircuitLoadingIndicator = () => {
    if (!loadingCircuits) return null;
    
    return (
      <div className="circuit-loading-indicator">
        <div className="loading-spinner"></div>
        <span>Loading circuit data...</span>
      </div>
    );
  };

  return (
    <div className="map-container">
      <MapContainer
        key={satelliteView ? "satellite" : "osm"}
        center={[33.9, -117.5]}
        zoom={10}
        scrollWheelZoom={true}
        style={{ height: "100%", width: "100%" }}
      >
        {/* Tile layer */}
        <TileLayer
          url={satelliteView ? 
            "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" : 
            "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          }
          attribution={satelliteView ? 
            '&copy; <a href="https://www.esri.com/en-us/arcgis/products/arcgis-online/overview">Esri</a>' : 
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          }
        />
        
        {/* Circuit lines */}
        <CircuitLinesLayer 
          selectedEntities={selectedEntities} 
          circuitData={circuitData} 
        />

        {/* Depot markers */}
        {depotCheckbox && (
          <DepotMarkersLayer 
            depots={depotList}
            selectedEntities={selectedEntities}
            getMarkerIcon={getMarkerIcon}
            handleMarkerClick={handleMarkerClick}
          />
        )}

        {/* Charger markers */}
        {chargerCheckbox && (
          <ChargerMarkersLayer 
            chargers={chargerList}
            selectedChargers={selectedChargers}
            getMarkerIcon={getMarkerIcon}
            handleChargerClick={handleChargerClick}
          />
        )}

        {/* Routes */}
        {routeCheckbox && (
          <RoutesLayer 
            routeData={selectedRoutes}
            highlightedRoute={highlightedRoute}
            routeColorMap={routeColorMap}
            setRouteColorMap={setRouteColorMap}
          />
        )}
      </MapContainer>
      
      {/* Circuit loading indicator */}
      <CircuitLoadingIndicator />
      
      {/* Map controls overlay */}
      <MapControls
        routeCheckbox={routeCheckbox}
        setRouteCheckbox={setRouteCheckbox}
        depotCheckbox={depotCheckbox}
        setDepotCheckbox={setDepotCheckbox}
        chargerCheckbox={chargerCheckbox}
        setChargerCheckbox={setChargerCheckbox}
        satelliteView={satelliteView}
        setSatelliteView={setSatelliteView}
      />
    </div>
  );
};